import { useCallback } from 'react';
import { OneOfChildElements, Project } from '@shared/models/project.model';
import {
  addRecipes,
  ExportObject,
  getCorrespondingProductRecord,
  getCorrespondingRecipeRecord,
  getImportedRecipeIdsToSoftDelete,
  getJSONFromFile,
  getProductsToAdd,
  getRecipesToAdd,
  printRecord,
  replaceProjectRecipeIds,
} from '../helpers/import.helpers';
import { getSelectedOrganization } from '@client/store/organization';
import { Recipe } from '@shared/models/recipe.model';
import { mapFilterRecord } from '@shared/helpers/object.helpers';
import { asManualCopy } from '@shared/helpers/element-copy.helpers';
import {
  getProductIdsInRecipes,
  getRecipesUsedInProject,
} from '@shared/helpers/recipe.helpers';
import {
  isGreaterOrEqualVersion,
  toLookup,
} from '@shared/helpers/utils.helpers';
import {
  getCreateRecipe,
  getDeleteRecipe,
  getRecipeLookup,
} from '@client/store/recipe/recipe.hook';
import { ProductRecord } from '@shared/models/product.model';
import {
  getProjectProductsRecord,
  replaceProductIds,
  updateProductsInProject,
} from '@shared/helpers/product.helpers';
import { getCreateProduct, getProductsLookup } from '@client/store/product';
import { createProject } from '@shared/helpers/project-factory.helpers';
import {
  flattenElements,
  getAllBuildingVersions,
} from '@shared/helpers/recursive-element.helpers';
import { migrateProposalsInProject } from '@shared/helpers/proposal-migration.helpers';
import { validateProject, validateRecipeProducts } from '@shared/validation';
import { objectPromiseAll } from '@shared/helpers/promise.helpers';
import { TMP_PROJECT_ID } from '@shared/constants';
import { useConfig } from '../providers/ConfigProvider';
import { useUIState } from '@client/store/ui';
import { useUserId } from './use-user.hooks';
import { FileWithPath } from 'react-dropzone';
import { useEditProjectDialogStore } from '../../features/projects/EditProject/edit-project.store';

/**
 * Import element with connected recipes and products.
 * Will add missing products and recipes to the target organization
 * and remap current IDs to the newly created items.
 * Note: This will not add the project in the store.
 * @param element
 * @param recipes
 * @returns A new element ready to add
 */
export const useImportElement = (): ((
  element: OneOfChildElements,
  recipes: Recipe[],
  products?: ProductRecord,
) => Promise<OneOfChildElements>) => {
  const importProject = useProjectWithImportedProductsAndRecipes();

  return useCallback(
    async (element, recipes, products) => {
      const project = createProject(
        {
          owner: TMP_PROJECT_ID,
          versions: [{ elements: [element] }],
        },
        false,
      );

      // Make a dummy import to import all recipes and products
      const importedProject = await importProject(project, recipes, products);
      const importedVersion = getAllBuildingVersions(importedProject)[0];
      const importedElement = importedVersion?.elements[0];

      const flattenedImportedElements = flattenElements(importedElement);
      const flattenedOriginalElements = flattenElements(element);

      if (
        flattenedImportedElements.length !== flattenedOriginalElements.length
      ) {
        throw new Error('Import error. Element count mismatch');
      }
      if (!importedElement) {
        throw new Error('Import error. No imported elements found');
      }

      // A pasted element is a manual copy — never a claim on an import row.
      return asManualCopy(importedElement);
    },
    [importProject],
  );
};

/**
 * Import project with recipes and products.
 * Will add missing products and recipes to the target organization
 * and remap current IDs to the newly created items.
 * Note: This will not add the project in the store.
 * @param project
 * @param recipes
 * @returns A new project ready to add
 */
export const useProjectWithImportedProductsAndRecipes = (): ((
  project: Project,
  recipes: Recipe[],
  products?: ProductRecord,
) => Promise<Project>) => {
  const importProducts = useImportProducts();
  const importRecipes = useImportRecipes();

  return useCallback(
    async (project, recipes, products) => {
      const { project: updatedProject, productMap } = await importProducts(
        project,
        recipes,
        products,
      );

      return await importRecipes(updatedProject, recipes, productMap);
    },
    [importProducts, importRecipes],
  );
};

const useImportProducts = (): ((
  project: Project,
  recipes: Recipe[],
  products?: ProductRecord,
) => Promise<{ project: Project; productMap: ProductRecord }>) => {
  return useCallback(async (project, recipes, products) => {
    const productsLookup = getProductsLookup();
    const organization = getSelectedOrganization(true);

    // Legacy exports still carry v1 proposal rows; migrate before validateProject (same order as server `projectsService.create`).
    project = migrateProposalsInProject(project);

    // Make sure project contains all products
    validateRecipeProducts(project, recipes);

    // From another organization, set it to current one
    if (!project.organizations?.includes(organization)) {
      project.organizations = [organization];
    }

    const importedProductRecord = products ?? getProjectProductsRecord(project);

    // A map of all products that are already available in the target organization.
    // Note that the keys are the id's of imported products
    const correspondingProducts = getCorrespondingProductRecord(
      importedProductRecord,
      productsLookup,
    );

    // Products that should be added
    const productsToAdd = getProductsToAdd(
      importedProductRecord,
      correspondingProducts,
      organization,
      project.owner,
    );

    const addedProductsMap = await getAddedProductsMap(productsToAdd);

    // // Products which can be mapped to an existing ID
    const productsToRemap = mapFilterRecord(
      correspondingProducts,
      (product, id) => (product.id !== id ? product : undefined),
    );

    const addedProductsLookup = toLookup(Object.values(addedProductsMap));

    // All product id's that have new ids (<old id, new product>)
    const productMap: ProductRecord = {
      ...productsToRemap,
      ...addedProductsMap,
    };

    const allProductsWithNewIds = {
      ...productsLookup,
      ...addedProductsLookup,
    };
    const allProductsWithOldToNewIds = {
      ...allProductsWithNewIds,
      ...productMap,
    };

    // Use the custom products that are already available in the target organization
    project = replaceProductIds(project, productMap);

    // Update the product records in all versions including all products from recipes too
    project = updateProductsInProject(project, allProductsWithNewIds, recipes);

    printRecord('Added products', addedProductsLookup);
    printRecord('Reused products', productsToRemap);

    return {
      project: validateProject(project),
      productMap: validateProductMapForRecipes(
        allProductsWithOldToNewIds,
        recipes,
      ),
    };
  }, []);
};

/**
 * Add products and return a map of the added products <old id, new product>
 * @param products
 * @returns
 */
const getAddedProductsMap = async (
  products: ProductRecord,
): Promise<ProductRecord> => {
  const createProduct = getCreateProduct();
  return objectPromiseAll(
    mapFilterRecord(products, (product) => createProduct(product)),
  );
};

const validateProductMapForRecipes = (
  productMap: ProductRecord,
  recipes: Recipe[],
): ProductRecord => {
  const recipeProducts = getProductIdsInRecipes(...recipes);

  recipeProducts.forEach((id) => {
    if (!productMap[id]) {
      throw new Error(
        `ProductMap validation error. Product with id ${id} is missing in the product map`,
      );
    }
  });
  return productMap;
};

const useImportRecipes = (): ((
  project: Project,
  recipes: Recipe[],
  correspondingProducts: ProductRecord,
) => Promise<Project>) => {
  return useCallback(async (project, recipes, correspondingProducts) => {
    const createRecipe = getCreateRecipe();
    const deleteRecipe = getDeleteRecipe();
    const recipeLookup = getRecipeLookup();
    const organization = getSelectedOrganization(true);

    // Make sure project contains all products
    validateRecipeProducts(project, recipes);

    // Only import recipes that are used in the project (previously we exported all)
    recipes = getRecipesUsedInProject(project, recipes);

    const importedRecipeRecord = toLookup(recipes);

    // All recipes that are already available in the target organization.
    const correspondingRecipes = getCorrespondingRecipeRecord(
      recipes,
      recipeLookup,
    );

    // Recipes which can be mapped to an existing ID
    const recipesToRemap = mapFilterRecord(
      correspondingRecipes,
      (recipe, id) => (recipe.id !== id ? recipe : undefined),
    );

    // All missing recipes
    const recipesToAdd = getRecipesToAdd(
      importedRecipeRecord,
      correspondingRecipes,
      correspondingProducts,
      project.owner,
      organization,
    );

    const addedRecipes = await addRecipes(
      recipesToAdd,
      organization,
      createRecipe,
    );

    project = replaceProjectRecipeIds(project, {
      ...recipesToRemap,
      ...addedRecipes,
    });

    // Re-soft-delete recipes that were deleted in the source. They are re-created
    // live (create ignores deleted_at) so references resolve, then hidden again.
    // Best-effort: a failed re-delete must not fail the whole import (the recipe
    // stays visible but the project still resolves), so use allSettled.
    const recipeIdsToSoftDelete = getImportedRecipeIdsToSoftDelete(
      importedRecipeRecord,
      addedRecipes,
    );
    const softDeleteResults = await Promise.allSettled(
      recipeIdsToSoftDelete.map((id) => deleteRecipe(id)),
    );
    softDeleteResults.forEach((result, index) => {
      if (result.status === 'rejected') {
        console.error(
          `Failed to re-soft-delete imported recipe ${recipeIdsToSoftDelete[index]}`,
          result.reason,
        );
      }
    });

    printRecord('Added recipes', addedRecipes);
    printRecord('Reused recipes', recipesToRemap);

    return project;
  }, []);
};

/**
 * Import projects from JSON files.
 * @returns A function that takes an array of Files and imports them as projects.
 */
export const useImportProject = () => {
  const { set: setEditProjectDialog } = useEditProjectDialogStore();
  const { selectedProjectFolderId } = useUIState('selectedProjectFolderId');
  const [config] = useConfig();
  const owner = useUserId();

  const getProjectWithImportedProductsAndRecipes =
    useProjectWithImportedProductsAndRecipes();

  return useCallback(
    async (files: readonly FileWithPath[]): Promise<void> => {
      if (!files.length) {
        throw new Error('No files selected');
      }

      for (const file of files) {
        const {
          recipes,
          version,
          project: jsonProject,
        }: ExportObject = await getJSONFromFile(file);

        const fileName = file.name.slice(0, file.name.indexOf('.json'));

        if (!jsonProject || !recipes || !version) {
          throw new Error('Invalid file');
        }

        // Keep this up to date (bump if big model changes are made)
        if (
          !isGreaterOrEqualVersion(version, config.version, {
            patch: Number.MAX_SAFE_INTEGER,
          })
        ) {
          throw new Error(
            `Project version must be newer than "${config.version}", please re-export the project.`,
          );
        }

        let project: Project = {
          ...jsonProject,
          owner,
          name: fileName,
          parent_id: selectedProjectFolderId ?? null,
        };

        project = await getProjectWithImportedProductsAndRecipes(
          project,
          recipes,
        );

        setEditProjectDialog({ type: 'project', mode: 'create', project });
      }
    },
    [
      config.version,
      owner,
      selectedProjectFolderId,
      getProjectWithImportedProductsAndRecipes,
      setEditProjectDialog,
    ],
  );
};
